import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, Download, CheckCircle2 } from 'lucide-react';
import { TableData, formatCellValue } from './utils/tableParser';
import { useRealtimeExportStatus } from '@/hooks/useRealtimeExportStatus';

interface ChatTableProps {
  tableData: TableData;
  className?: string;
  totalAvailable?: number | null;
  exportId?: string;
  connectionId?: string;
  organisationId?: string;
}

type SortDirection = 'asc' | 'desc' | null;

const ChatTable: React.FC<ChatTableProps> = ({ 
  tableData, 
  className = '',
  totalAvailable,
  exportId,
  connectionId,
  organisationId
}) => {
  const [sortColumn, setSortColumn] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Monitor real-time export status
  const { status: exportStatus } = useRealtimeExportStatus({ 
    exportId: exportId || '',
    fallbackPollingInterval: 3000
  });

  const sortedRows = useMemo(() => {
    let processedRows = [...tableData.rows];

    // Apply sorting
    if (sortColumn !== null && sortDirection) {
      processedRows.sort((a, b) => {
        const aVal = a[sortColumn] || '';
        const bVal = b[sortColumn] || '';
        
        // Try to parse as numbers for numeric sorting
        const aNum = parseFloat(aVal.replace(/[$,]/g, ''));
        const bNum = parseFloat(bVal.replace(/[$,]/g, ''));
        
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        // String sorting
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      });
    }

    return processedRows;
  }, [tableData.rows, sortColumn, sortDirection]);

  const handleSort = (columnIndex: number) => {
    if (sortColumn === columnIndex) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(columnIndex);
      setSortDirection('asc');
    }
  };

  const handleDownload = () => {
    if (exportStatus?.download_url) {
      window.open(exportStatus.download_url, '_blank');
    }
  };

  return (
    <div data-testid="chat-table" className={`space-y-3 ${className}`}>
      {/* Table Controls */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-gray-500">
          {totalAvailable 
            ? `Showing ${tableData.rows.length} of ${totalAvailable.toLocaleString()} SKUs`
            : `${tableData.rows.length} rows`
          }
        </span>
        
        {exportId && totalAvailable && totalAvailable > tableData.rows.length && (
          <Button
            data-testid="chat-export"
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={exportStatus?.status !== 'completed'}
            className="h-8 px-2 text-xs"
          >
            {exportStatus?.status === 'completed' ? (
              <>
                <CheckCircle2 size={12} className="mr-1 text-green-600" />
                Download Full Export
              </>
            ) : exportStatus?.status === 'failed' ? (
              <>
                <Download size={12} className="mr-1" />
                Export Failed
              </>
            ) : (
              <>
                <Download size={12} className="mr-1 animate-pulse" />
                Preparing Export...
              </>
            )}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="border rounded-md overflow-hidden">
        <div className="max-h-96 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                {tableData.headers.map((header, index) => (
                  <TableHead
                    key={index}
                    className="font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort(index)}
                  >
                    <div className="flex items-center gap-1">
                      {header}
                      <ArrowUpDown 
                        size={12} 
                        className={`transition-opacity ${
                          sortColumn === index ? 'opacity-100' : 'opacity-30'
                        }`}
                      />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((row, rowIndex) => (
                <TableRow 
                  key={rowIndex}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {row.map((cell, cellIndex) => (
                    <TableCell 
                      key={cellIndex} 
                      className="text-sm font-mono"
                    >
                      {formatCellValue(cell, tableData.headers[cellIndex])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

    </div>
  );
};

export default ChatTable;
